


import { Component, Prop, Vue } from "vue-property-decorator";

import { Configuration, Plans, Subscriptions } from "App";
import { DeepCopy, EventBus } from "App/IOC";
import {
    Price, Product, Plan, GlobalConfiguration
} from "App/Models";

import { ProvideReactive } from "Web/Libs/VuePropertyDecorators";
import PlanForm from "Web/Views/Products/Single/Plans/Form.vue";
import ProductPrices from "Web/Views/Products/Single/Prices/Index.vue";
import ProductPlan from "Web/Views/Products/Single/Plans/Plan.vue";

@Component({
    components: {
        PlanForm,
        ProductPrices,
        ProductPlan
    }
})
export default class PropductPlans extends Vue {
    @Prop() readonly Product!: Product;

    @ProvideReactive()
    private configuration?: GlobalConfiguration = {} as GlobalConfiguration;

    private showForm: boolean = false;
    private busy: boolean = false;

    /* Index of the plan column currently being dragged (drag-and-drop reordering). */
    private dragIndex: number | null = null;

    /* Plans that still have Active subscriptions on an outdated price snapshot. Drives the
       "prices not applied to everyone" column label; refreshed after the bulk migration runs. */
    private outdatedPlanIds: number[] = [];

    async created(): Promise<void> {
        this.configuration = undefined;

        /* Refresh the "prices not applied" labels both when a new version is published (a plan
           becomes outdated) and when the bulk migration runs (it stops being outdated). */
        EventBus.Subscribe( "SubscriptionsMigrated", this.loadOutdatedPlans );
        EventBus.Subscribe( "PriceVersionsChanged", this.loadOutdatedPlans );
        this.loadOutdatedPlans();

        const [ globalConfiguration, error ] = await Configuration.Get();

        if( error !== undefined || globalConfiguration === undefined ) {
            EventBus.Publish( "Error", error );
            return;
        }

        this.configuration = globalConfiguration;
    }

    private beforeDestroy(): void {
        EventBus.Unsubscribe( "SubscriptionsMigrated", this.loadOutdatedPlans );
        EventBus.Unsubscribe( "PriceVersionsChanged", this.loadOutdatedPlans );
    }

    private async loadOutdatedPlans(): Promise<void> {
        const [ planIds ] = await Subscriptions.OutdatedPlanIds();

        this.outdatedPlanIds = planIds;
    }

    private isPlanOutdated( planId: number ): boolean {
        return this.outdatedPlanIds.indexOf( planId ) !== -1;
    }

    private async create( record: Plan ) {
        record.Product = DeepCopy( this.Product );

        this.$emit( "Create", record );
    }

    private createPrice( price: Price, plan: Plan ): void {
        this.$emit( "CreatePrice", price, plan );
    }

    private onDragStart( index: number ): void {
        if( this.busy )
            return;

        this.dragIndex = index;
    }

    private onDrop( index: number ): void {
        const from = this.dragIndex;
        this.dragIndex = null;

        if( from === null || from === index )
            return;

        this.reorder( from, index );
    }

    /* Move the dragged plan to the drop position then rewrite every Sequence (1..n) so the order
       persists, same persistence path as moveUp. */
    private async reorder( from: number, to: number ): Promise<void> {
        if( this.busy )
            return;

        this.busy = true;

        const plans = DeepCopy( this.Product.Plans );

        const [ moved ] = plans.splice( from, 1 );
        plans.splice( to, 0, moved );

        let counter = 1;

        for( const record of plans ) {
            /* Reset the sequence so Plans.Update detects a change for every record. */
            record.Sequence = 0;

            const updatedRecord = DeepCopy( record );
            updatedRecord.Sequence = counter++;

            const [ , error ] = await Plans.Update( record, updatedRecord );

            if( error !== undefined ) {
                EventBus.Publish( "Error", error );
                this.busy = false;
                return;
            }
        }

        this.busy = false;

        this.$emit( "Refresh" );
    }

    private async setDefault( record: Plan ): Promise<void> {
        if( this.busy )
            return;

        this.busy = true;

        const updatedRecord = DeepCopy( record );
        updatedRecord.IsDefault = true;

        const [ , error ] = await Plans.Update( record, updatedRecord );


        if( error !== undefined ) {
            EventBus.Publish( "Error", error );
            return;
        }

        this.busy = false;

        this.$emit( "Refresh" );
    }
}
